/**
 * The seven capabilities, described once so the screens are generated rather than hand-copied.
 *
 * Every capability screen is the same shape — a small create form over a list — and the only things
 * that differ are the endpoint, the fields, and the columns. Encoding that difference as data keeps
 * seven screens from drifting into seven subtly different ones, and keeps a new capability a config
 * entry rather than a new file. Alerting is the one with two sections (the alert and where it
 * escalates), which is why a capability owns an ARRAY of sections.
 *
 * The GET and POST share a path for every section — the same URL lists and creates — mirroring the
 * API routes exactly.
 */

import type { ReactNode } from "react";
import { relative } from "./api";

export interface Field {
  name: string;
  label: string;
  type: "text" | "number" | "password";
  /** Prefilled so a reviewer can submit the form without inventing values. */
  default?: string;
  width?: number;
}

export interface Column {
  header: string;
  render: (row: Record<string, unknown>) => ReactNode;
}

export interface Section {
  heading: string;
  blurb: string;
  /** Lists (GET) and creates (POST) at the same path. */
  path: string;
  /** The array key in the GET response, e.g. "signals". */
  listKey: string;
  /** Singular noun for the empty state, e.g. "signal". */
  itemNoun: string;
  fields: Field[];
  columns: Column[];
}

export interface CapabilityConfig {
  id: string;
  title: string;
  description: string;
  /** Overrides the default "native store / vendor seam" note under the title, when a capability's
   *  wiring differs (e.g. the model router is gated on a key-encryption key, not a vendor URL). */
  note?: string;
  sections: Section[];
}

/** A created-at column, since every table has one and reads it the same way. */
function when(header: string): Column {
  return { header, render: (r) => <span className="muted">{relative(String(r["createdAt"]))}</span> };
}

/** A plain text cell. */
function text(header: string, key: string, muted = false): Column {
  return { header, render: (r) => (muted ? <span className="muted">{String(r[key])}</span> : String(r[key])) };
}

export const CAPABILITY_CONFIGS: CapabilityConfig[] = [
  {
    id: "observability",
    title: "Observability",
    description: "Traces, metrics and dashboards across a product's services — read, not owned.",
    sections: [{
      heading: "Record a signal",
      blurb: "A trace/metric/event datapoint. Lands in the native per-tenant store; a vendor seam (Datadog) takes over when configured.",
      path: "/v1/observability/signals",
      listKey: "signals",
      itemNoun: "signal",
      fields: [
        { name: "name", label: "name", type: "text", default: "checkout.p95" },
        { name: "kind", label: "kind", type: "text", default: "metric", width: 120 },
        { name: "value", label: "value", type: "number", default: "42", width: 100 },
      ],
      columns: [text("Name", "name"), text("Kind", "kind", true), text("Value", "value", true), when("Recorded")],
    }],
  },
  {
    id: "logging",
    title: "Logging",
    description: "Structured log ingestion and search, orchestrated around the product's existing sink.",
    sections: [{
      heading: "Ingest a log line",
      blurb: "One structured line. Native store by default; a vendor seam (Elastic) takes over when configured.",
      path: "/v1/logging/lines",
      listKey: "lines",
      itemNoun: "line",
      fields: [
        { name: "level", label: "level", type: "text", default: "warn", width: 120 },
        { name: "message", label: "message", type: "text", default: "disk 91% full" },
      ],
      columns: [text("Level", "level", true), text("Message", "message"), when("Ingested")],
    }],
  },
  {
    id: "monitoring",
    title: "Monitoring",
    description: "Health and SLO checks against the registered service targets.",
    sections: [{
      heading: "Record a health check",
      blurb: "A check result against a target. Native store by default; a vendor seam (Grafana) takes over when configured.",
      path: "/v1/monitoring/checks",
      listKey: "checks",
      itemNoun: "check",
      fields: [
        { name: "target", label: "target", type: "text", default: "orders-api" },
        { name: "status", label: "status", type: "text", default: "up", width: 120 },
        { name: "latencyMs", label: "latency ms", type: "number", default: "12", width: 110 },
      ],
      columns: [text("Target", "target"), text("Status", "status", true), text("Latency (ms)", "latencyMs", true), when("Checked")],
    }],
  },
  {
    id: "alerting",
    title: "Alerting",
    description: "Routing and escalation of signals to the right on-call, deduplicated.",
    sections: [
      {
        heading: "Raise an alert",
        blurb: "A signal awaiting routing. Native store by default; a vendor seam (PagerDuty) takes over when configured.",
        path: "/v1/alerting/alerts",
        listKey: "alerts",
        itemNoun: "open alert",
        fields: [
          { name: "severity", label: "severity", type: "text", default: "critical", width: 130 },
          { name: "summary", label: "summary", type: "text", default: "checkout down" },
        ],
        columns: [text("Severity", "severity", true), text("Summary", "summary"), text("Status", "status", true), when("Raised")],
      },
      {
        heading: "Add an escalation route",
        blurb: "Where a severity escalates to.",
        path: "/v1/alerting/routes",
        listKey: "routes",
        itemNoun: "route",
        fields: [
          { name: "severity", label: "severity", type: "text", default: "critical", width: 130 },
          { name: "channel", label: "channel", type: "text", default: "#oncall" },
        ],
        columns: [text("Severity", "severity", true), text("Channel", "channel"), when("Added")],
      },
    ],
  },
  {
    id: "api_gateway",
    title: "API Gateway",
    description: "Edge routing, auth and rate policy for a product's public surface.",
    sections: [{
      heading: "Publish an edge route",
      blurb: "A public path mapped to an upstream. Native store by default; a vendor seam (Kong) takes over when configured.",
      path: "/v1/api-gateway/routes",
      listKey: "routes",
      itemNoun: "route",
      fields: [
        { name: "path", label: "path", type: "text", default: "/v1/orders" },
        { name: "upstream", label: "upstream", type: "text", default: "orders:9090" },
        { name: "methods", label: "methods", type: "text", default: "GET,POST", width: 130 },
      ],
      columns: [text("Path", "path"), text("Upstream", "upstream", true), text("Methods", "methods", true), when("Published")],
    }],
  },
  {
    id: "traffic",
    title: "Traffic",
    description: "Progressive delivery, shaping and failover between service versions and regions.",
    sections: [{
      heading: "Shift a traffic split",
      blurb: "A primary/canary weighting. Native store by default; a vendor seam (LaunchDarkly) takes over when configured.",
      path: "/v1/traffic/splits",
      listKey: "splits",
      itemNoun: "split",
      fields: [
        { name: "service", label: "service", type: "text", default: "checkout" },
        { name: "primaryWeight", label: "primary %", type: "number", default: "90", width: 110 },
        { name: "canaryWeight", label: "canary %", type: "number", default: "10", width: 110 },
      ],
      columns: [text("Service", "service"), text("Primary %", "primaryWeight", true), text("Canary %", "canaryWeight", true), when("Shifted")],
    }],
  },
  {
    id: "test_suites",
    title: "Test Suites",
    description: "Synthetic and contract suites run against targets, with results as evidence.",
    sections: [{
      heading: "Record a suite run",
      blurb: "A run kept as evidence. Native store by default; a vendor seam (Playwright) takes over when configured.",
      path: "/v1/test-suites/runs",
      listKey: "runs",
      itemNoun: "run",
      fields: [
        { name: "suite", label: "suite", type: "text", default: "checkout-smoke" },
        { name: "status", label: "status", type: "text", default: "passed", width: 120 },
        { name: "passed", label: "passed", type: "number", default: "10", width: 100 },
        { name: "failed", label: "failed", type: "number", default: "0", width: 100 },
      ],
      columns: [text("Suite", "suite"), text("Status", "status", true), text("Passed", "passed", true), text("Failed", "failed", true), when("Recorded")],
    }],
  },
  {
    id: "model_router",
    title: "Model Router",
    description: "Route LLM completions through OpenRouter with per-tenant provider keys.",
    note: "Keys are encrypted at rest with the server's key-encryption key (MODEL_ROUTER_KEK); if it " +
      "is unset the capability refuses to store a key rather than keep it in clear. The secret is " +
      "never shown again after you add it — only its last four characters. Completions are run via " +
      "the API (POST /v1/model-router/complete); this screen manages the keys.",
    sections: [{
      heading: "Add a provider key",
      blurb: "A tenant may hold many keys per provider; completions spread across the enabled ones.",
      path: "/v1/model-router/keys",
      listKey: "keys",
      itemNoun: "key",
      fields: [
        { name: "provider", label: "provider", type: "text", default: "openrouter", width: 150 },
        { name: "label", label: "label", type: "text", default: "primary", width: 150 },
        { name: "secret", label: "secret key (sk-or-…)", type: "password" },
      ],
      columns: [
        text("Provider", "provider", true),
        text("Label", "label"),
        { header: "Key", render: (r) => <span className="muted">…{String(r["last4"])}</span> },
        { header: "State", render: (r) => (r["disabled"] ? <span className="muted">disabled</span> : "active") },
        when("Added"),
      ],
    }],
  },
];

export function configById(id: string | undefined): CapabilityConfig | undefined {
  return CAPABILITY_CONFIGS.find((c) => c.id === id);
}
